import { readContract } from '@wagmi/core'
import { useEffect, useMemo, useState } from 'react'

import { ELECTIONS_ADDRESS } from '@/constants/blockchain'
import OMMAElection from '@/contracts/OMMAElection'
import { useCustomContractRead } from '@/hooks/useCustomContract'
import { useAuth } from '@/plugins/auth'
import { getWagmiConfig, l1NativeConfig, l1NativeWagmiConfig } from '@/plugins/auth/config'
import { CustomError, parseError } from '@/utils/helpers'
import { emptyBalance, processBalance, ProcessedBalance } from '@/utils/utils'

import { useChainValues } from './useChain'
import { ElectionStats, fetchElectionStats } from './useElectionsServer'
import { CandidateStatus, CandidateStatusMap } from './useGovernance'

export type CandidateResults = {
  candidate: string
  status: CandidateStatus
  cid: string
  votes: ProcessedBalance
  root: string
}

export type ElectionResultsHook = {
  finalized: boolean
  candidateResults: CandidateResults[]
  stats: ElectionStats
  numVoters: number
  verifiedVoters: number
  userVerified: boolean
  userTotal: ProcessedBalance
  isLoading: boolean
  error: CustomError
}

export const EmptyElectionResults: ElectionResultsHook = {
  finalized: false,
  candidateResults: [],
  stats: [],
  numVoters: 0,
  verifiedVoters: 0,
  userVerified: false,
  userTotal: emptyBalance,
  isLoading: false,
  error: {
    call: '',
    msg: undefined,
    isError: false,
  },
}

const useGetCandidateResults = (
  blockNumber: number,
  electionIdx: number,
  candidates?: string[]
) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [candidateResults, setCandidateResults] = useState<CandidateResults[]>([])
  const config = getWagmiConfig()

  // Get rewards in batches if necessary
  const getCandidateResults = async (cands: string[]): Promise<void> => {
    try {
      setIsLoading(true)
      const values = await Promise.all(
        cands.map(candidate =>
          readContract(config, {
            chainId: l1NativeConfig.chainId,
            address: ELECTIONS_ADDRESS,
            abi: OMMAElection,
            functionName: 'candidateResults',
            args: [BigInt(electionIdx), candidate as `0x${string}`],
          })
        )
      )
      // Combine data
      const data = cands.map((candidate, idx) => ({
        candidate,
        status: CandidateStatusMap[values[idx][0]],
        cid: values[idx][1],
        votes: processBalance(values[idx][2]),
        root: values[idx][3],
      }))
      // Sort by votes
      data.sort((a, b) => Number(b.votes.value - a.votes.value))
      setCandidateResults(data)
      setIsError(false)
    } catch (error: any) {
      setIsError(true)
    }
    setIsLoading(false)
  }

  // Fetch on new blocks or changes to idx and candidates
  useEffect(() => {
    if (candidates && electionIdx) {
      getCandidateResults(candidates)
    }
  }, [blockNumber, candidates, electionIdx])

  return {
    candidateResults,
    isLoading,
    error: { call: 'getCandidateResults', msg: isError ? '' : undefined, isError },
  }
}

export const useElectionResults = (electionIdx: number): ElectionResultsHook => {
  const { address } = useAuth()

  // Get block number
  const { blockNumber } = useChainValues(l1NativeWagmiConfig.id)

  // Stats
  const [stats, setStats] = useState<ElectionStats>([])
  const [isLoadingStats, setIsLoadingStats] = useState(false)
  const [errorStats, setErrorStats] = useState<CustomError>({
    call: '',
    msg: undefined,
    isError: false,
  })

  const fetchStats = async () => {
    if (electionIdx === undefined || electionIdx === 0) {
      return
    }
    setIsLoadingStats(true)
    try {
      const fetchedStats = await fetchElectionStats(electionIdx)
      setStats(fetchedStats)
    } catch (error: any) {
      setErrorStats({ call: 'fetchStats', msg: error.message, isError: true })
    }
    setIsLoadingStats(false)
  }

  // Get results finalized
  const {
    value: finalizedRaw,
    isLoading: isLoadingFinalized,
    error: errorFinalized,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      abi: OMMAElection,
      functionName: 'resultsFinalized',
      args: [BigInt(electionIdx)],
    },
    blockNumber,
    electionIdx !== undefined && electionIdx !== 0
  )
  const finalized = useMemo(() => finalizedRaw || false, [finalizedRaw])

  // Get election candidates
  const {
    value: candidatesRaw,
    isLoading: isLoadingCandidates,
    error: errorCandidates,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      abi: OMMAElection,
      functionName: 'getElectionCandidates',
      args: [BigInt(electionIdx)],
    },
    blockNumber,
    electionIdx !== undefined && electionIdx !== 0
  )
  const candidates = useMemo(
    () => (candidatesRaw ? candidatesRaw.map(cand => cand as string) : []),
    [candidatesRaw]
  )

  // Build candidate results list
  const {
    candidateResults,
    isLoading: isLoadingCandidateResults,
    error: errorCandidateResults,
  } = useGetCandidateResults(blockNumber, electionIdx, candidates)

  // Get number of voters
  const {
    value: numVotersRaw,
    isLoading: isLoadingNumVoters,
    error: errorNumVoters,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      abi: OMMAElection,
      functionName: 'numVoters',
      args: [BigInt(electionIdx)],
    },
    blockNumber,
    electionIdx !== undefined && electionIdx !== 0
  )
  const numVoters = useMemo(() => Number(numVotersRaw || 0n), [numVotersRaw])

  // Get number of verified voters
  const {
    value: verifiedVotersRaw,
    isLoading: isLoadingVerifiedVoters,
    error: errorVerifiedVoters,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      abi: OMMAElection,
      functionName: 'verifiedVoters',
      args: [BigInt(electionIdx)],
    },
    blockNumber,
    electionIdx !== undefined && electionIdx !== 0
  )
  const verifiedVoters = useMemo(() => Number(verifiedVotersRaw || 0n), [verifiedVotersRaw])

  // Get user data
  const {
    value: userData,
    isLoading: isLoadingUserData,
    error: errorUserData,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      abi: OMMAElection,
      functionName: 'voterData',
      args: [BigInt(electionIdx), address as `0x${string}`],
    },
    blockNumber,
    electionIdx !== undefined && electionIdx !== 0
  )

  // User verified
  const userVerified = useMemo(() => (userData ? userData[0] : false), [userData])

  // User total
  const userTotal = useMemo(() => processBalance(userData ? userData[1] : 0n), [userData])

  // Loading
  const isLoading = useMemo(
    () =>
      isLoadingFinalized ||
      isLoadingCandidates ||
      isLoadingCandidateResults ||
      isLoadingNumVoters ||
      isLoadingVerifiedVoters ||
      isLoadingUserData ||
      isLoadingStats,
    [
      isLoadingFinalized,
      isLoadingCandidates,
      isLoadingCandidateResults,
      isLoadingNumVoters,
      isLoadingVerifiedVoters,
      isLoadingUserData,
      isLoadingStats,
    ]
  )

  // Error
  const errors = [
    errorFinalized,
    errorCandidates,
    errorCandidateResults,
    errorNumVoters,
    errorVerifiedVoters,
    errorUserData,
    errorStats,
  ]
  const error = useMemo(() => parseError(errors), errors)

  // Fetch stats
  useEffect(() => {
    fetchStats()
  }, [electionIdx])

  return {
    finalized,
    candidateResults,
    stats,
    numVoters,
    verifiedVoters,
    userVerified,
    userTotal,
    isLoading,
    error: {
      call: `electionResults.${error.call}`,
      msg: error.msg,
      isError: error.isError,
    },
  }
}
